import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { invoicesApi, ApiError } from "@/api";
import type { InvoiceDashboardResponse, InvoiceDashboardRow } from "@/types";
import { Spinner, Pill, Toast, useToast } from "@/components/ui";
import { invoiceStatusTone, invoiceDaysChip, humanise } from "@/lib/jobs";
import { formatMoney } from "@/lib/money";
import { formatDateDMY } from "@/lib/format";
import { NewInvoiceFlow } from "./NewInvoiceFlow";
import "./invoices-dashboard.css";

type View = "outstanding" | "overdue" | "mtd";

/**
 * Cross-job Invoices dashboard (migrated from Retool). One GET /dashboard on
 * load → KPI tiles + every invoice across all jobs. Read-only: the segmented
 * filter is purely client-side, rows drill through to the job, and the copy
 * actions put the Xero / payment URLs on the clipboard (Xero deep-links need
 * the user's own authenticated tab — so we copy rather than open).
 */
export function InvoicesDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<InvoiceDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [view, setView] = useState<View>("outstanding");
  const { toast, showToast } = useToast();
  const [showNew, setShowNew] = useState(false);

  const load = useCallback((opts: { initial?: boolean } = {}) => {
    let cancelled = false;
    if (opts.initial) setLoading(true);
    invoicesApi
      .getInvoiceDashboard()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the invoices dashboard.");
      })
      .finally(() => {
        if (!cancelled && opts.initial) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load({ initial: true }), [load]);

  // The nav "New invoice" button routes here with { create: true } — open the
  // job-picker flow and strip the flag.
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowNew(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  const rows = useMemo(() => {
    const all = data?.invoices ?? [];
    if (view === "outstanding") return all.filter((i) => i.is_outstanding);
    if (view === "overdue") return all.filter((i) => i.is_overdue);
    return all.filter((i) => i.issued_this_month);
  }, [data, view]);

  function copy(url: string, message: string) {
    navigator.clipboard
      .writeText(url)
      .then(() => showToast(message))
      .catch(() => window.prompt("Copy this link:", url));
  }

  if (loading) {
    return (
      <section className="inv-dash">
        <div className="jobs-list__loading"><Spinner label="Loading invoices…" /></div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="inv-dash">
        <h1>Invoices</h1>
        <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error || "No data."}</p>
      </section>
    );
  }

  const { kpis } = data;
  const SEGMENTS: { value: View; label: string }[] = [
    { value: "outstanding", label: "Outstanding" },
    { value: "overdue", label: `Overdue (${kpis.overdue_count})` },
    { value: "mtd", label: "Invoiced This Month" },
  ];

  return (
    <section className="inv-dash">
      <header className="inv-dash__head">
        <h1>Invoices</h1>
      </header>

      <dl className="inv-dash__kpis">
        <Kpi label="Outstanding" value={kpis.total_outstanding} />
        <Kpi label="Invoiced this month" value={kpis.mtd_billed} />
        <Kpi label="Overdue" value={kpis.total_overdue} />
      </dl>

      <div className="inv-dash__filter" role="tablist" aria-label="Invoice view">
        {SEGMENTS.map((s) => (
          <button
            key={s.value}
            type="button"
            role="tab"
            aria-selected={view === s.value}
            className={`inv-dash__seg${view === s.value ? " is-on" : ""}`}
            onClick={() => setView(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="jobs-list__empty">No invoices to show.</p>
      ) : (
        <div className="inv-dash__table-wrap">
          <table className="inv-dash__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Due date</th>
                <th>Days</th>
                <th>Invoice no.</th>
                <th className="num">Job no.</th>
                <th>Job name</th>
                <th>Client</th>
                <th className="num">Total</th>
                <th className="num">Paid</th>
                <th className="num">Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <InvoiceRow key={row.id} row={row} onOpen={() => navigate(`/staff/jobs/${row.job_id}`)} onCopy={copy} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew ? (
        <NewInvoiceFlow
          onClose={() => setShowNew(false)}
          onCreated={(res) => {
            setShowNew(false);
            showToast(`Invoice ${res.xero_invoice_number} raised — awaiting payment.`);
            load();
          }}
        />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="inv-dash__kpi">
      <dt>{label}</dt>
      <dd className="numeric">{formatMoney(value)}</dd>
    </div>
  );
}

function InvoiceRow({
  row,
  onOpen,
  onCopy,
}: {
  row: InvoiceDashboardRow;
  onOpen: () => void;
  onCopy: (url: string, message: string) => void;
}) {
  const days = invoiceDaysChip(row.days_to_due);
  return (
    <tr className="inv-dash__row" onClick={onOpen} title="Open job">
      <td>{formatDateDMY(row.issue_date)}</td>
      <td>{formatDateDMY(row.due_date)}</td>
      <td><span className={`job-chip job-chip--${days.tone}`}>{days.label}</span></td>
      <td className="numeric">{row.xero_invoice_number || "—"}</td>
      <td className="num numeric">{row.job_number}</td>
      <td>{row.job_name || "—"}</td>
      <td>{row.client_name || "—"}</td>
      <td className="num numeric">{formatMoney(row.total)}</td>
      <td className="num numeric">{formatMoney(row.amount_paid)}</td>
      <td className="num numeric">{formatMoney(row.balance)}</td>
      <td>{row.status ? <Pill tone={invoiceStatusTone(row.status)}>{humanise(row.status)}</Pill> : <span className="inv-dash__muted">—</span>}</td>
      <td>
        <div className="inv-dash__actions" onClick={(e) => e.stopPropagation()}>
          {row.xero_url ? (
            <a className="inv-dash__copy" href={row.xero_url} target="_blank" rel="noreferrer" title="Open the invoice in Xero">
              Xero ↗
            </a>
          ) : null}
          {row.online_invoice_url ? (
            <button type="button" className="inv-dash__copy" title="Copy payment link" onClick={() => onCopy(row.online_invoice_url, "Payment link copied.")}>
              Pay ⧉
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
